<script lang="ts" setup>
    import { computed, onMounted, ref, watch } from 'vue'
    import { useDebounceFn } from '@vueuse/core'
    import { Module, ModulHandbuch } from '@/js/model'
    import MultiEdit from '@/js/components/Common/MultiEdit.vue'
    import { FetchParams } from '@/js/utils/fetch-params'
    import {
        useModuleStore,
        useFachbereichStore,
        useStudiengangStore,
        useUserStore,
        useModulHandbuchStore,
        useAlertStore
    } from '@/js/stores'
    import ContentCollapse from '@/js/components/Common/ContentCollapse.vue'
    import { itemsPerPageOptions, itemsPerPageText, loadingText, noItemsText } from '@/js/utils'
    import { useStore } from 'vuex'

    const page = ref<number>(1)
    const itemsPerPage = ref<number>(10)

    const search = ref<string>('')
    const searchName = ref<string>('')
    const showArchivedOnly = ref<boolean>(false)
    const selectedStudiengangId = ref<string | null>(null)
    const searchSemester = ref<string>('')
    const loading = ref<boolean>(true)
    const serverItems = ref<Module[]>([])
    const totalItems = ref<number>(0)

    const loadItems = async ({ page, itemsPerPage, sortBy }: FetchParams) => {
        const params: FetchParams = {
            page,
            itemsPerPage,
            sortBy,
            ['search[modulname]']: searchName.value,
            isArchived: showArchivedOnly.value ? 'true' : 'false',
            studiengangId: selectedStudiengangId.value ?? undefined,
            semester: searchSemester.value || undefined
        }

        loading.value = true
        const result = await modulStore.fetchPaginatedModules(params)

        serverItems.value = result.data['hydra:member']
        totalItems.value = result.data['hydra:totalItems'] ?? 0
        loading.value = false
    }

    const headers = ref([
        {
            key: 'modulname',
            title: 'Modulname',
            sortable: true
        },
        {
            key: 'kurzName',
            title: 'Kurzname',
            sortable: true
        },
        {
            key: 'fachbereichId',
            title: 'Fachbereich',
            sortable: true
        },
        {
            key: 'studiengangId',
            title: 'Studiengang',
            sortable: true
        },
        {
            key: 'semester',
            title: 'Semester',
            sortable: false
        },
        {
            key: 'ectsWorkloadValid',
            title: 'ECTS Workload'
        },
        {
            key: 'modulverantwortliche',
            title: 'Modulverantwortliche'
        },
        {
            key: 'archived',
            title: 'Archiviert',
            sortable: false
        },
        {
            key: 'actions',
            title: 'Aktionen',
            sortable: false,
            align: 'center'
        }
    ])

    const fachbereichStore = useFachbereichStore()
    const studiengangStore = useStudiengangStore()
    const modulStore = useModuleStore()
    const userStore = useUserStore()
    const modulHandbuchStore = useModulHandbuchStore()
    const alertStore = useAlertStore()

    const showConfirmArchiveDialog = ref(false)
    const modulToArchive = ref<Module | null>(null)

    const modulToArchiveMhbs = computed(() => {
        if (!modulToArchive.value) return []
        return modulHandbuchStore.getModulhandbuecher.filter((mhb: ModulHandbuch) =>
            mhb.modulIds?.includes((modulToArchive.value as any).modulId)
        )
    })

    const isLoading = computed(() => {
        return modulStore.isLoading || userStore.isLoading
    })

    const autoCompleteData = computed(() => {
        const users = userStore.users
        return users.map((user) => {
            return {
                id: user.userId,
                label: `${user['titel']} ${user['vorname']} ${user['nachname']}`
            }
        })
    })

    function getFormattedFachbereich(fachbereichId: string) {
        const fachbereich = fachbereichStore.getFachbereichById(fachbereichId)
        if (!fachbereich) {
            return ''
        }

        return `${fachbereich.kurzName} - ${fachbereich.name}`
    }

    function getFormattedSemester(semester: { typ?: string | null; jahr?: number | null } | null | undefined): string {
        if (!semester?.typ || !semester?.jahr) return ''
        if (semester.typ === 'SoSe') return `SoSe ${semester.jahr}`
        return `WiSe ${semester.jahr}/${String((semester.jahr + 1) % 100).padStart(2, '0')}`
    }

    function getFormattedStudiengang(studiengangId: string) {
        return studiengangStore.getStudiengangById(studiengangId)?.kurzName
    }

    function getIdMapForUsers(userIds: string[]) {
        return userIds.map((userId: string) => {
            const user = userStore.getUserById(userId)
            if (!user) return {}

            return {
                id: user?.userId,
                label: `${user?.titel} ${user?.vorname} ${user?.nachname}`
            }
        })
    }

    function confirmArchiveModul(item: Module) {
        modulToArchive.value = item
        showConfirmArchiveDialog.value = true
    }

    async function archiveModul() {
        if (!modulToArchive.value) return
        await modulStore.archiveModul((modulToArchive.value as any).modulId)
        showConfirmArchiveDialog.value = false
        if (!modulStore.hasError()) {
            alertStore.success('Modul wurde archiviert.', 'Info')
        } else {
            alertStore.error('Fehler beim Archivieren des Moduls.', 'Fehler!')
        }
        search.value = String(Date.now())
    }

    function saveModulVerantwortliche(modulId, event) {
        modulStore.saveModulverantwortliche(modulId, event)
        search.value = String(Date.now())
    }
    function saveModulName(modulId, event) {
        modulStore.saveModulName(modulId, event)
        search.value = String(Date.now())
    }

    watch(
        searchName,
        useDebounceFn(() => {
            search.value = String(Date.now())
        }, 500)
    )

    watch(
        searchSemester,
        useDebounceFn(() => {
            search.value = String(Date.now())
        }, 500)
    )

    watch(showArchivedOnly, () => {
        search.value = String(Date.now())
    })

    watch(selectedStudiengangId, () => {
        search.value = String(Date.now())
    })

    onMounted(async () => {
        await Promise.all([
            fachbereichStore.fetchFachbereiche(),
            studiengangStore.fetchStudiengaenge(),
            modulHandbuchStore.fetchModulHandbuecher()
        ])
    })
</script>

<template>
    <div>
        <h2 class="mt-3 mx-2">Verwaltung der Module</h2>

        <content-collapse title="Liste der Module" collapse-key="modul-control-list">
            <template #badge>
                <v-progress-circular v-if="isLoading" indeterminate color="silvergrey" class="ml-2" size="14" />
                <v-badge v-else color="silvergrey" text-color="black" inline :content="`${totalItems}`" />
            </template>
            <v-data-table-server
                :loading="isLoading"
                :headers="headers"
                data-test="modules-table"
                :items="serverItems"
                :search="search"
                class="mb-0"
                density="compact"
                :page="page"
                :items-length="totalItems"
                :items-per-page="itemsPerPage"
                :items-per-page-options="itemsPerPageOptions"
                :items-per-page-text="itemsPerPageText"
                :no-data-text="noItemsText"
                :loading-text="loadingText"
                mobile-breakpoint="sm"
                disable-sort
                hide-default-footer
                @update:options="loadItems"
            >
                <template v-slot:loading>
                    <v-skeleton-loader type="table-row@6"></v-skeleton-loader>
                </template>
                <template v-slot:top>
                    <div class="border-b-sm mb-4">
                        <div class="d-flex justify-end border-b-sm mb-4">
                            <v-data-table-footer
                                class="border-box border-black"
                                :items-per-page-text="itemsPerPageText"
                                :itemsPerPageOptions="itemsPerPageOptions"
                                show-current-page
                            />
                        </div>
                        <div class="d-flex flex-wrap justify-space-between ga-8 pb-4">
                            <v-text-field
                                class="py-3"
                                v-model="searchName"
                                label="Suche nach Modulnamen"
                                placeholder="Modulname"
                                variant="outlined"
                                density="compact"
                                hide-details="auto"
                                max-width="600"
                            />
                            <v-autocomplete
                                v-model="selectedStudiengangId"
                                :items="studiengangStore.studiengaenge"
                                item-title="kurzName"
                                item-value="studiengangId"
                                label="Studiengang"
                                placeholder="Alle Studiengänge"
                                variant="outlined"
                                density="compact"
                                hide-details="auto"
                                clearable
                                max-width="400"
                                class="py-3"
                            />
                            <v-text-field
                                class="py-3"
                                v-model="searchSemester"
                                label="Suche nach Semester"
                                placeholder="z.B. SoSe 2024"
                                variant="outlined"
                                density="compact"
                                hide-details="auto"
                                max-width="300"
                            />
                            <v-switch
                                v-model="showArchivedOnly"
                                label="Nur archivierte Module"
                                color="primary"
                                density="compact"
                                hide-details
                                class="py-3"
                            />
                        </div>
                    </div>
                </template>
                <template #item.modulname="{ item }">
                    <CommonSingleEdit
                        :entry="item.modulname"
                        title=""
                        :edit-key="`modul-control-name-${item.modulId}`"
                        :allow-edit="true"
                        :show-title="false"
                        @text-saved="saveModulName(item.modulId, $event)"
                    />
                </template>
                <template #item.semester="{ item }">
                    <span class="text-no-wrap">{{ getFormattedSemester(item.semester) }}</span>
                </template>
                <template #item.studiengangId="{ item }">
                    {{ getFormattedStudiengang(item.studiengangId) }}
                </template>
                <template #item.fachbereichId="{ item }">
                    {{ getFormattedFachbereich(item.fachbereichId) }}
                </template>
                <template #item.ectsWorkloadValid="{ item }">
                    <v-icon v-if="item" size="24" icon="mdi-check" />
                    <v-icon v-else size="24" icon="mdi-minus-circle" />
                </template>
                <template #item.modulverantwortliche="{ item }">
                    <MultiEdit
                        title=""
                        :edit-key="`modul-control-mv-${item.modulId}`"
                        :entries="getIdMapForUsers(item.modulverantwortliche)"
                        :auto-complete-data="autoCompleteData"
                        :allow-edit="true"
                        :show-title="false"
                        @text-saved="saveModulVerantwortliche(item.modulId, $event)"
                    />
                </template>
                <template #item.archived="{ item }">
                    <div class="d-flex justify-center">
                        <v-icon v-if="item.archived" size="sm" icon="mdi-archive" color="grey" />
                        <v-icon v-else size="sm" icon="mdi-minus" />
                    </div>
                </template>
                <template #item.actions="{ item }">
                    <v-btn icon="mdi-delete" title="Modul archivieren" size="sm" @click="confirmArchiveModul(item)" />
                </template>
            </v-data-table-server>

            <v-dialog v-model="showConfirmArchiveDialog" max-width="60%">
                <v-card>
                    <v-card-title>Modul „{{ (modulToArchive as any)?.modulname }}" archivieren?</v-card-title>
                    <v-card-text>
                        <p>Das Modul wird aus folgenden Modulhandbüchern entfernt:</p>
                        <v-list v-if="modulToArchiveMhbs.length > 0" density="compact">
                            <v-list-item
                                v-for="mhb in modulToArchiveMhbs"
                                :key="mhb.modulHandbuchId"
                                prepend-icon="mdi-book-open-variant"
                            >
                                {{ getFormattedStudiengang(mhb.studiengangId) }} – {{ mhb.semester }}
                            </v-list-item>
                        </v-list>
                        <p v-else>Das Modul ist keinem Modulhandbuch zugewiesen.</p>
                    </v-card-text>
                    <v-card-actions>
                        <v-btn variant="outlined" @click="showConfirmArchiveDialog = false">Abbrechen</v-btn>
                        <v-btn color="error" @click="archiveModul">Archivieren</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </content-collapse>
    </div>
</template>

<style scoped lang="scss"></style>
